import { useEffect, useState, type FunctionComponent } from 'react';
import type { HomePageImageFieldsFragment, VideoFieldsFragment } from '../../../__generated__/graphql-client-types';
import { NO_IMAGE_URL } from '../../../constants/home/general';
import { windowIsNotSmall } from '../../../helpers/breakpoints/breakpoint.helpers';
import { generateCloudinaryUrl, type CloudinaryOptions } from '../../../helpers/cloudinary-helper/cloudinary-helper';
import type { SplashCallToAction } from '../../../types/home.types';
import { EmbeddedVideo } from '../../common-components/embedded-video/embedded-video.component';
import { Link } from '../../common-components/link/link.component';
import { ctaButtonPosition, splashAspectRatio } from '../animated-splash/animated-splash.css';
import { CallToAction } from '../call-to-action/call-to-action.component';
import { MainImage } from '../home-splash/main-image/main-image.component';

const SPLASH_WIDTH = 1280;
const SPLASH_HEIGHT = 512;

const FALLBACK_IMAGE_OPTIONS: CloudinaryOptions = {
	width: SPLASH_WIDTH,
	height: SPLASH_HEIGHT,
	quality: 80,
	progressive: true,
	format: 'jpg',
	type: 'private'
};

export type AnimatedSplashProps = {
	image: HomePageImageFieldsFragment;
	fallbackImage: HomePageImageFieldsFragment;
	video?: VideoFieldsFragment | null;
	mobileImage?: HomePageImageFieldsFragment;
	backgroundTracking?: string | null;
	callToAction: Omit<SplashCallToAction, 'desktop'>;
};

export const AnimatedSplash: FunctionComponent<AnimatedSplashProps> = ({
	image,
	fallbackImage,
	video,
	mobileImage,
	backgroundTracking,
	callToAction
}) => {
	const [showVideo, setShowVideo] = useState<boolean>(false);
	const { foregroundTracking } = callToAction;
	const { url, tracking } = callToAction.link;
	const resolvedForegroundTracking = foregroundTracking ? foregroundTracking : tracking;
	const resolvedBackgroundTracking = backgroundTracking ? backgroundTracking : tracking;
	const fallbackImageSrc = generateCloudinaryUrl(fallbackImage.id, FALLBACK_IMAGE_OPTIONS);
	const hasNoImage = image.id === NO_IMAGE_URL;

	useEffect(() => {
		const isNotSmall = windowIsNotSmall();
		if (isNotSmall) {
			setShowVideo(true);
		}
	}, []);

	return (
		<section className="flex flex-column items-center w-100 min-h-100 relative pointer bg-theme-white" data-testid="animated-splash">
			<div className="flex flex-column items-center overflow-hidden w-100 relative br4" style={{ maxHeight: SPLASH_HEIGHT }}>
				{/* TODO: EFDC-33369 Replace with animated splash CTA */}
				<CallToAction
					callToAction={{
						...callToAction,
						desktop: {
							__typename: 'Image',
							id: 'content-tool/k4t1uhaoa3cfdjqkucla',
							imageType: 'private',
							description: '67867867867867',
							lowQualityPlaceholder: ''
						}
					}}
					foregroundTracking={resolvedForegroundTracking}
					containerClassName="dn flex-ns z-1"
					buttonPositionClassName={ctaButtonPosition}
				/>
				{hasNoImage ? (
					<div className={`relative w-100 ${splashAspectRatio}`}>
						<div className="absolute mh3 mh0-ns" style={{ inset: 0 }}>
							{/* TODO: Replace with dedicated Picture component once created */}
							<Link url={url}>
								<picture>
									{mobileImage && (
										<source
											media="(max-width: 767px)"
											srcSet={generateCloudinaryUrl(mobileImage.id, { type: 'private' })}
										/>
									)}
									{fallbackImage && <source media="(min-width: 768px)" srcSet={fallbackImageSrc} />}
									<img
										src={fallbackImageSrc}
										alt={fallbackImage.description}
										className="br4 max-h-100"
										width={SPLASH_WIDTH}
										height={SPLASH_HEIGHT}
									/>
								</picture>
							</Link>
						</div>
						{showVideo && video?.id && (
							<div className="absolute" style={{ inset: 0 }} data-testid="splash-video">
								<Link url={url} className="w-100 h-100">
									<div className="disableClickEvents">
										<EmbeddedVideo video={video} shouldLoadEagerly={true} />
									</div>
								</Link>
							</div>
						)}
					</div>
				) : image ? (
					<MainImage
						background={image}
						url={url}
						analyticsHook={resolvedBackgroundTracking}
						options={{ ...FALLBACK_IMAGE_OPTIONS, format: 'auto' }}
					/>
				) : null}
			</div>
		</section>
	);
};
